<template>
  <div
    class="conversation-details-wrap bg-neutral-50 dark:bg-slate-800 flex m-0 h-full min-h-0"
    :class="{ 'with-border-right': !isOnExpandedLayout }"
  >
    <div class="conversation-messages-wrap flex-1 flex flex-col min-h-0">
      <conversation-header
        v-if="currentChat && inbox"
        :chat="currentChat"
        :is-contact-panel-open="isContactPanelOpen"
        :show-back-button="isOnExpandedLayout"
        @contact-panel-toggle="onToggleContactPanel"
      />
      <woot-tabs
        v-if="dashboardApps.length && currentChat.id"
        :index="activeIndex"
        class="dashboard-app--tabs bg-white dark:bg-slate-900 -mt-px"
        :fill-last-item="false"
        @change="onDashboardAppTabChange"
      >
        <woot-tabs-item
          v-for="tab in dashboardAppTabs"
          :key="tab.key"
          :name="tab.name"
          :show-badge="false"
        />
      </woot-tabs>
      <div
        v-show="!activeIndex"
        class="flex bg-slate-25 dark:bg-slate-800 m-0 flex-1 min-h-0"
      >
        <messages-container
          v-if="currentChat && inbox"
          :inbox-id="inboxId"
          :is-contact-panel-open="isContactPanelOpen"
          @contact-panel-toggle="onToggleContactPanel"
        />
        <empty-state v-else :is-on-expanded-layout="isOnExpandedLayout" />
      </div>
      <dashboard-app-frame
        v-for="(dashboardApp, index) in dashboardApps"
        v-show="activeIndex - 1 === index"
        :key="currentChat.id + '-' + dashboardApp.id"
        :is-visible="activeIndex - 1 === index"
        :config="dashboardApps[index].content"
        :position="index"
        :current-chat="currentChat"
      />
    </div>
    <transition name="backdrop-fade">
      <div
        v-if="isDrawerMode && showContactPanel"
        class="fixed inset-0 z-40 bg-black/40"
        @click="onToggleContactPanel"
      />
    </transition>
    <transition
      :css="false"
      @before-enter="onDrawerBeforeEnter"
      @enter="onDrawerEnter"
      @leave="onDrawerLeave"
    >
      <div v-if="isDrawerMode && showContactPanel" :class="sidebarClasses">
        <contact-panel-v2
          :prevent-block-contact="!currentChat.can_reply && isAWhatsAppChannel"
          :in-conversation-box="true"
          :on-toggle="onToggleContactPanel"
        />
      </div>
    </transition>
    <div v-if="!isDrawerMode && showContactPanel" :class="sidebarClasses">
      <contact-panel-v2
        :prevent-block-contact="!currentChat.can_reply && isAWhatsAppChannel"
        :in-conversation-box="true"
        :on-toggle="onToggleContactPanel"
      />
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import ContactPanelV2 from 'dashboard/routes/dashboard/conversation/ContactPanelV2/Index.vue';
import ConversationHeader from 'dashboard/routes/dashboard/conversation/ConversationHeader/Index.vue';
import DashboardAppFrame from 'dashboard/components/widgets/DashboardApp/Frame.vue';
import EmptyState from 'dashboard/components/widgets/conversation/EmptyState/EmptyState.vue';
import MessagesContainer from './MessagesContainer';
import alertMixin from 'shared/mixins/alertMixin';
import inboxMixin from 'shared/mixins/inboxMixin';

export default {
  components: {
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    MessagesContainer,
    ContactPanelV2,
  },

  mixins: [alertMixin, inboxMixin],
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return { activeIndex: 0, windowWidth: window.innerWidth };
  },
  computed: {
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.currentChat.inbox_id);
    },
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
    }),
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.dashboardApps.map(dashboardApp => ({
          key: `dashboard-${dashboardApp.id}`,
          name: dashboardApp.title,
        })),
      ];
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
    sidebarClasses() {
      if (this.isDrawerMode) {
        return [
          'conversation-sidebar-wrap',
          'rtl:border-r',
          'border-slate-50',
          'dark:border-slate-700',
          'overflow-y-auto',
          'fixed',
          'right-0',
          'rtl:right-auto',
          'rtl:left-0',
          'top-0',
          'h-full',
          'w-[350px]',
          'max-w-full',
          'z-50',
          'bg-white',
          'dark:bg-slate-800',
          'shadow-2xl',
        ];
      }
      return [
        'conversation-sidebar-wrap',
        'rtl:border-r',
        'border-slate-50',
        'dark:border-slate-700',
        'overflow-auto',
        'basis-full',
        'sm:basis-[34%]',
        'sm:min-w-[13rem]',
        'sm:max-w-[350px]',
        'h-auto',
        'z-10',
        'flex-shrink-0',
        'flex-grow-0',
      ];
    },
    isDrawerMode() {
      return this.windowWidth <= 1024;
    },
  },
  watch: {
    'currentChat.inbox_id': {
      handler(newVal) {
        if (newVal) {
          this.$store.dispatch('inboxAssignableAgents/fetchByInboxId', newVal);
        }
      },
      immediate: true,
    },
    'currentChat.id'() {
      if (this.isAnEmailChannel) {
        this.$emit('contact-panel-off');
      }
      this.fetchLabels();
      this.activeIndex = 0;
    },
  },
  mounted() {
    if (this.isAnEmailChannel) {
      this.$emit('contact-panel-off');
    }
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
    window.addEventListener('resize', this.handleWindowResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleWindowResize);
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onToggleContactPanel() {
      this.$emit('contact-panel-toggle');
    },
    onDashboardAppTabChange(index) {
      // d99d
      if (index) {
        const [dac] = this.dashboardApps[index - 1]?.content || [];
        if (dac?.type === 'link') {
          const dynParam =
            this.currentChat.custom_attributes[dac.link_attribute?.key];
          if (dynParam === undefined) {
            this.showAlert(
              this.$t(
                'INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.NO_ATTRIBUTE_ON_LINK_ERROR',
                { attributeName: dac.link_attribute?.key }
              )
            );
          } else {
            const url = `${dac.url}/${dynParam || ''}`;
            window.open(url, '_blank');
          }
          return;
        }
      }
      // .
      this.activeIndex = index;
    },
    handleWindowResize() {
      this.windowWidth = window.innerWidth;
    },
    onDrawerBeforeEnter(el) {
      el.style.transform = 'translateX(100%)';
    },
    onDrawerEnter(el, done) {
      el.getBoundingClientRect(); // force reflow so browser sees initial state
      el.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      el.style.transform = '';
      el.addEventListener(
        'transitionend',
        () => {
          el.style.transition = '';
          done();
        },
        { once: true }
      );
    },
    onDrawerLeave(el, done) {
      el.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      el.style.transform = 'translateX(100%)';
      el.addEventListener(
        'transitionend',
        () => {
          el.style.transition = '';
          done();
        },
        { once: true }
      );
    },
  },
};
</script>
<style lang="scss" scoped>
.conversation-details-wrap {
  @apply flex flex-row min-w-0 w-full;

  &.with-border-right {
    @apply border-r border-slate-50 dark:border-slate-700;
  }
}

.conversation-messages-wrap {
  /* Above the contact sidebar (z-10) and the conversations list, so the shadow
     below and the header dropdowns both render over them. Kept well under the
     modal layer (z-9990) so modals still cover the conversation panel. */
  @apply relative z-20;

  /* Symmetric on purpose: renders identically in LTR and RTL, since
     box-shadow offsets do not flip with the text direction. */
  box-shadow:
    4px 0 4px 0 rgba(163, 163, 163, 0.1),
    -4px 0 4px 0 rgba(163, 163, 163, 0.1);
}

.conversation-sidebar-wrap {
  /* Symmetric on purpose: renders identically in LTR and RTL, since
     box-shadow offsets do not flip with the text direction. */
  box-shadow:
    4px 0 4px 0 rgba(163, 163, 163, 0.1),
    -4px 0 4px 0 rgba(163, 163, 163, 0.1);

  @apply min-w-0;

  &::v-deep .contact--panel,
  &::v-deep .contact-detail {
    @apply w-full h-full min-w-0 max-w-full;
  }
}

.backdrop-fade-enter-active,
.backdrop-fade-leave-active {
  transition: opacity 0.25s ease;
}

.backdrop-fade-enter-from,
.backdrop-fade-leave-to {
  opacity: 0;
}
</style>
